import * as React from 'react';

import { Tag } from '@coursera/cds-core';

import _t from 'i18n!nls/product-card';

export type TrendingNowPillVariant = 'none' | 'gradient-background' | 'solid-background';

// MERCH-3316: dedicated slug list for the "Trending now" pill test.
// https://docs.google.com/spreadsheets/d/1AxnjBpMUYKKOasHHWH00r9uYv5Ef7O8lCBGJxDZERrg/edit?gid=219402167#gid=219402167
const TRENDING_NOW_PILL_SLUGS = new Set([
  'google-project-management',
  'google-cybersecurity',
  'google-data-analytics',
  'google-it-support',
  'facebook-social-media-marketing',
  'google-digital-marketing-ecommerce',
  'google-ux-design',
  'financial-modeling-and-analysis',
  'technical-support-fundamentals',
  'excel',
  'stanford-statistics',
  'finance-accounting',
  'wharton-business-foundations',
  'project-management-foundations',
  'learn-sql-basics-data-science',
  'foundations-data',
  'project-initiation-google',
  'positivepsychology',
  'ask-questions-make-decisions',
  'nasm-personal-fitness-trainer',
  'python-3-programming',
  'fullstack-development',
  'master-microsoft-office-365-power-platform',
  'foundations-of-cybersecurity',
  'introduction-psychology',
  'sciwrite',
  'meta-database-engineer',
  'unilever-supply-chain-data-analyst',
  'medical-neuroscience',
  'google-business-intelligence',
  'leading-teams',
  'certificado-de-carrera-de-google-marketing-digital-e-e-commerce',
  'financial-markets-global',
  'os-power-user',
  'food-and-health',
  'learn-spanish',
  'computer-networking',
  'human-resource-management',
  'algorithms',
  'google-it-automation',
  'genomic-data-science',
  'negotiation',
  'adp-compensation-benefits-analyst',
  'negotiation-skills',
  'tesol',
  'getting-started-with-google-workspace',
  'authentic-communication-with-confidence-and-impact',
  'anatomy',
  'microsoft-excel-learn-with-projects',
  'mafash',
  'thinklikeacfo',
  'project-planning-google',
  'futures-thinking',
  'brand',
  'microsoft-introduction-to-c-plus-plus-programming',
  'become-an-emt',
  'tally-bookkeeper',
  'investment-portolio-management',
  'boost-your-leadership-impact',
  'social-psychology',
  'foundations-of-digital-marketing-and-e-commerce',
  'ibm-project-manager',
  'drug-hunting-discovery-and-development',
  'wealth-management-specialization',
  'gis',
  'microsoft-public-relations',
  'discrete-mathematics',
  'wharton-business-financial-modeling',
  'six-sigma-fundamentals',
  'microsoft-365-fundamentals',
  'filmmaking',
  'digital-marketing',
  'clinical-trials-operations',
  'ibm-cybersecurity-analyst',
  'astro',
  'introduction-to-cosmetic-science',
  'workday-human-capital-management',
  'procurement-sourcing',
  'business-communication',
  'data-science-foundations-r',
  'good-with-words',
  'introduction-to-data-analysis-using-python',
  'cvshealth-call-center-customer-service',
  'data-science-python',
  'ibm-full-stack-javascript-developer',
  'learnquest-certified-scrum-master',
]);

export const isTrendingNowPillSlug = (slug: string): boolean => TRENDING_NOW_PILL_SLUGS.has(slug);

// MERCH-3316: hex/token values from the approved design.
// CDS's default hasIcon padding-inline-start (--cds-spacing-25, 2px) reads as too tight against
// this icon's own glyph, which — unlike AIGenerateIcon — has ink running close to its left edge.
// paddingLeft below overrides just that side to give the icon breathing room from the pill border.
const trendingNowPillVariantStyle: Record<'gradient-background' | 'solid-background', React.CSSProperties> = {
  'gradient-background': {
    background:
      'linear-gradient(90deg, var(--MDS-3-Expressive-Yellow-Yellow-500-AA, #D65D00) -9.21%, var(--MDS-3-Expressive-Pink-Pink-700-AAA, #9C1A84) 100%)',
    color: 'var(--cds-color-neutral-primary-invert)',
    border: 'none',
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingLeft: 'var(--cds-spacing-75)',
  },
  'solid-background': {
    backgroundColor: 'var(--cds-color-feedback-background-error-weak)',
    color: 'var(--cds-color-feedback-error)',
    border: 'none',
    paddingTop: '1px',
    paddingBottom: '1px',
    paddingLeft: 'var(--cds-spacing-75)',
  },
};

// Uses `fill="currentColor"` so the icon always matches the pill's own text color, set above.
const TrendingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2.03383 12.2833C1.9116 12.1611 1.84771 12.0194 1.84216 11.8583C1.8366 11.6972 1.89494 11.5556 2.01716 11.4333L5.43383 8.01667C5.66716 7.78334 5.95049 7.66667 6.28383 7.66667C6.61716 7.66667 6.90049 7.78334 7.13383 8.01667L8.81716 9.7L12.3505 6.18334H11.0005C10.8305 6.18334 10.688 6.12617 10.573 6.01184C10.458 5.8975 10.4005 5.75584 10.4005 5.58684C10.4005 5.41784 10.458 5.275 10.573 5.15834C10.688 5.04167 10.8305 4.98334 11.0005 4.98334H13.8005C13.9705 4.98334 14.113 5.04084 14.228 5.15584C14.343 5.27084 14.4005 5.41334 14.4005 5.58334V8.38334C14.4005 8.55334 14.3433 8.69584 14.229 8.81084C14.1147 8.92584 13.973 8.98334 13.804 8.98334C13.635 8.98334 13.4922 8.92745 13.3755 8.81567C13.2588 8.70378 13.2005 8.56523 13.2005 8.4V7.03334L9.66716 10.5667C9.43383 10.8 9.15049 10.9167 8.81716 10.9167C8.48383 10.9167 8.20049 10.8 7.96716 10.5667L6.28383 8.86667L2.88383 12.2833C2.7616 12.4056 2.61994 12.4667 2.45883 12.4667C2.29771 12.4667 2.15605 12.4056 2.03383 12.2833Z"
      fill="currentColor"
    />
  </svg>
);

export const getTrendingNowPillLabel = () => _t('Trending right now');

/**
 * Builds the "Trending right now" experimental status tag, including its pre-rendered pill, for the
 * given Statsig variant. Returns undefined when the variant is 'none'/unset — unlike the other
 * pill tests, there's no plain fallback rendering for this tag: the control group sees no pill
 * at all, since the test is about whether adding the pill helps, not which treatment is best.
 */
export const getTrendingNowExperimentalTag = (variant?: TrendingNowPillVariant) => {
  if (!variant || variant === 'none') {
    return undefined;
  }

  const tag = getTrendingNowPillLabel();

  return {
    tag,
    tagName: 'Trending right now',
    tagRenderer: (
      <Tag
        color="default"
        priority="secondary"
        variant="status"
        icon={<TrendingIcon />}
        style={trendingNowPillVariantStyle[variant]}
      >
        {tag}
      </Tag>
    ),
  };
};
